import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useGameStore, Companion } from "../store/gameStore";

const CompanionTraining: React.FC = () => {
  const { 
    companions, 
    coins,
    startCompanionTraining, 
    startCompanionJourney, 
    levelUpCompanion,
    getCompanionLevel,
    getCompanionExperience,
    getCompanionExperienceForNextLevel,
    canCompanionLevelUp,

    gardenAreas
  } = useGameStore();

  const [selectedCompanion, setSelectedCompanion] = useState<Companion | null>(null);
  const [activityType, setActivityType] = useState<"training" | "journey">("training");
  const [journeyType, setJourneyType] = useState<"treasure" | "renovation" | "experience">("treasure");
  const [selectedArea, setSelectedArea] = useState<keyof typeof gardenAreas>("lawn");
  
  // Filter-Zustand
  const [filters, setFilters] = useState({
    rarity: 'all' as 'all' | 'common' | 'rare' | 'epic' | 'legendary' | 'godly' | 'mystic',
    ability: 'all' as 'all' | 'clickMultiplier' | 'coinPerSecond' | 'gemChance' | 'xpBonus' | 'gardenBonus' | 'autoClickPower' | 'upgradeDiscount' | 'prestigeBonus' | 'criticalHit' | 'healing' | 'shield' | 'counterAttack' | 'lifeSteal' | 'armorPenetration',
    status: 'all' as 'all' | 'available' | 'busy',
    search: ''
  });
  
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);

  const getRarityColor = (type: Companion["type"]) => {
    switch (type) {
      case "legendary": return "from-yellow-400 to-orange-500";
      case "epic": return "from-purple-400 to-pink-500";
      case "rare": return "from-blue-400 to-cyan-500";
      case "common": return "from-gray-400 to-gray-600";
      default: return "from-gray-400 to-gray-600";
    }
  };

  const getRarityBorder = (type: Companion["type"]) => {
    switch (type) {
      case "legendary": return "border-yellow-400";
      case "epic": return "border-purple-400";
      case "rare": return "border-blue-400";
      case "common": return "border-gray-400";
      default: return "border-gray-400";
    }
  };

  const getRarityName = (type: Companion["type"]) => {
    switch (type) {
      case "legendary": return "Legendär";
      case "epic": return "Episch";
      case "rare": return "Selten";
      case "common": return "Gewöhnlich";
      default: return "Unbekannt";
    }
  };

  const isCompanionActive = (companion: Companion) => {
    return companions.active.some(c => c && c.id === companion.id);
  };

  const isGodlyLegendary = (companion: Companion) => {
    return companion.name.startsWith('★') && companion.name.endsWith('★');
  };

  const isMysticDivine = (companion: Companion) => {
    return companion.name.startsWith('★★★') && companion.name.endsWith('★★★');
  };

  const isCompanionBusy = (companion: Companion) => {
    return companion.isTraining || companion.isOnJourney || isCompanionActive(companion);
  };

  const getAbilityIcon = (type: string) => {
    switch (type) {
      case 'clickMultiplier': return '👆';
      case 'coinPerSecond': return '💰';
      case 'gemChance': return '💎';
      case 'xpBonus': return '⭐';
      case 'gardenBonus': return '🌱';
      case 'autoClickPower': return '🤖';
      case 'upgradeDiscount': return '🏷️';
      case 'prestigeBonus': return '🔄';
      case 'criticalHit': return '⚔️';
      case 'healing': return '❤️';
      case 'shield': return '🛡️';
      case 'counterAttack': return '↩️';
      case 'lifeSteal': return '🩸';
      case 'armorPenetration': return '🗡️';
      default: return '✨';
    }
  };

  const getAbilityName = (type: string) => {
    switch (type) {
      case 'clickMultiplier': return 'Klick-Bonus';
      case 'coinPerSecond': return 'Münzen/s';
      case 'gemChance': return 'Edelstein-Chance';
      case 'xpBonus': return 'Erfahrung';
      case 'gardenBonus': return 'Garten-Bonus';
      case 'autoClickPower': return 'Auto-Klick';
      case 'upgradeDiscount': return 'Upgrade-Rabatt';
      case 'prestigeBonus': return 'Prestige-Bonus';
      case 'criticalHit': return 'Kritischer Treffer';
      case 'healing': return 'Heilung';
      case 'shield': return 'Schild';
      case 'counterAttack': return 'Gegenangriff';
      case 'lifeSteal': return 'Lebensraub';
      case 'armorPenetration': return 'Rüstungsdurchschlag';
      default: return 'Unbekannt';
    }
  };

  const getAbilitySuffix = (type: string) => {
    switch (type) {
      case 'clickMultiplier':
      case 'gemChance':
      case 'xpBonus':
      case 'gardenBonus':
      case 'upgradeDiscount':
      case 'prestigeBonus':
      case 'lifeSteal':
        return '%';
      default:
        return '';
    }
  };

  const getCompanionStatus = (companion: Companion) => {
    if (isCompanionActive(companion)) return { text: "Aktiv", color: "bg-green-500" };
    if (companion.isTraining) return { text: "Training", color: "bg-blue-500" };
    if (companion.isOnJourney) return { text: "Auf Reise", color: "bg-purple-500" };
    return { text: "Verfügbar", color: "bg-gray-500" };
  };

  const getTimeRemaining = (companion: Companion) => {
    if (companion.isTraining && companion.trainingStartTime && companion.trainingDuration) {
      const startTime = new Date(companion.trainingStartTime);
      const endTime = new Date(startTime.getTime() + companion.trainingDuration * 60 * 1000);
      const now = new Date();
      const remainingMs = endTime.getTime() - now.getTime();
      
      if (remainingMs > 0) {
        const minutes = Math.ceil(remainingMs / (1000 * 60));
        return `${minutes} Min`;
      }
    }
    
    if (companion.isOnJourney && companion.journeyStartTime && companion.journeyDuration) {
      const startTime = new Date(companion.journeyStartTime);
      const endTime = new Date(startTime.getTime() + companion.journeyDuration * 60 * 60 * 1000);
      const now = new Date();
      const remainingMs = endTime.getTime() - now.getTime();
      
      if (remainingMs > 0) {
        const hours = Math.ceil(remainingMs / (1000 * 60 * 60));
        return `${hours} Std`;
      }
    }
    
    return null;
  };

  const handleStartTraining = (durationMinutes: number) => {
    if (selectedCompanion && startCompanionTraining(selectedCompanion.id, durationMinutes)) {
      setSelectedCompanion(null);
    }
  };

  const handleStartJourney = (durationHours: number) => {
    if (selectedCompanion) {
      const area = journeyType === "renovation" ? selectedArea : undefined;
      if (startCompanionJourney(selectedCompanion.id, journeyType, durationHours, area)) {
        setSelectedCompanion(null);
      }
    }
  };

  const handleLevelUp = (companion: Companion) => {
    if (canCompanionLevelUp(companion)) {
      levelUpCompanion(companion.id);
    }
  };

  // Filter-Funktionen
  const getFilteredCompanions = () => {
    return companions.discovered.filter(companion => {
      // Rarity Filter
      if (filters.rarity !== 'all') {
        if (filters.rarity === 'mystic' && !isMysticDivine(companion)) return false;
        if (filters.rarity === 'godly' && !isGodlyLegendary(companion)) return false;
        if (filters.rarity !== 'mystic' && filters.rarity !== 'godly' && companion.type !== filters.rarity) return false;
        if (filters.rarity !== 'mystic' && filters.rarity !== 'godly' && (isGodlyLegendary(companion) || isMysticDivine(companion))) return false;
      }

      // Ability Filter
      if (filters.ability !== 'all') {
        const hasAbility = companion.abilities.some(ability => ability.type === filters.ability);
        if (!hasAbility) return false;
      }

      // Status Filter
      if (filters.status !== 'all') {
        const isBusy = isCompanionBusy(companion);
        if (filters.status === 'available' && isBusy) return false;
        if (filters.status === 'busy' && !isBusy) return false;
      }

      // Search Filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const nameMatch = companion.name.toLowerCase().includes(searchLower);
        const abilityMatch = companion.abilities.some(ability => 
          ability.description.toLowerCase().includes(searchLower) ||
          ability.type.toLowerCase().includes(searchLower)
        );
        if (!nameMatch && !abilityMatch) return false;
      }

      return true;
    });
  };

  const resetFilters = () => {
    setFilters({
      rarity: 'all',
      ability: 'all', 
      status: 'all',
      search: ''
    });
  };

  const availableCompanions = getFilteredCompanions();

  const areaNames: Record<keyof typeof gardenAreas, string> = {
    lawn: "🌱 Rasen",
    flowerBed: "🌸 Blumenbeet",
    pond: "🌊 Teich",
    terrace: "🏡 Terrasse",
    greenhouse: "🏠 Gewächshaus",
    fountain: "⛲ Brunnen",
    orchard: "🍎 Obstgarten",
    veggiePatch: "🥕 Gemüsebeet",
    roseGarden: "🌹 Rosengarten",
    japaneseGarden: "🎋 Zen-Garten",
    wildflowerMeadow: "🌼 Wildblumenwiese",
    sculptureGarden: "🗿 Skulpturengarten",
    enchantedGrove: "🧚 Verzauberter Hain",
    crystalCave: "💎 Kristallhöhle",
    moonlightGarden: "🌙 Mondschein-Garten",
    dragonLair: "🐉 Drachenhort",
    goldMine: "⛏️ Goldmine",
    treasureVault: "💰 Schatzkammer",
    alchemyLab: "⚗️ Alchemie-Labor",
    magicForge: "🔥 Magische Schmiede"
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Filter & Suche */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl border border-blue-200 shadow-lg overflow-hidden">
        {/* Header mit Toggle-Button */}
        <div className="p-4 flex items-center justify-between cursor-pointer" onClick={() => setIsFilterExpanded(!isFilterExpanded)}>
          <div className="flex items-center gap-3">
            <div className="text-2xl">🔍</div>
            <div>
              <h3 className="text-xl font-bold text-blue-800">Filter & Suche</h3>
              <p className="text-sm text-blue-600">
                {(filters.rarity !== 'all' || filters.ability !== 'all' || filters.status !== 'all' || filters.search) 
                  ? 'Aktive Filter gesetzt' 
                  : 'Keine Filter aktiv'}
              </p>
            </div>
          </div>
          <div className={`text-2xl text-blue-600 transition-transform duration-300 ${isFilterExpanded ? 'rotate-180' : ''}`}>
            ▼
          </div>
        </div>

        {/* Collapsible Content */}
        <motion.div
          initial={false}
          animate={{ height: isFilterExpanded ? 'auto' : 0, opacity: isFilterExpanded ? 1 : 0 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          className="overflow-hidden"
        >
          <div className="p-4 space-y-4 border-t border-blue-200">
            {/* Suchleiste */}
            <div>
              <label className="block text-sm font-medium text-blue-800 mb-2">Suche:</label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Nach Namen oder Fähigkeiten suchen..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  className="w-full px-4 py-3 pl-10 bg-white border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-400">
                  🔍
                </div>
              </div>
            </div>

            {/* Filter-Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {/* Rarity Filter */}
              <div>
                <label className="block text-sm font-medium text-blue-800 mb-2">Seltenheit:</label>
                <select
                  value={filters.rarity}
                  onChange={(e) => setFilters(prev => ({ ...prev, rarity: e.target.value as any }))}
                  className="w-full px-3 py-2 bg-white border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">Alle</option>
                  <option value="common">Gewöhnlich</option>
                  <option value="rare">Selten</option>
                  <option value="epic">Episch</option>
                  <option value="legendary">Legendär</option>
                  <option value="godly">Göttlich</option>
                  <option value="mystic">Mystisch</option>
                </select>
              </div>

              {/* Ability Filter */}
              <div>
                <label className="block text-sm font-medium text-blue-800 mb-2">Fähigkeit:</label>
                <select
                  value={filters.ability}
                  onChange={(e) => setFilters(prev => ({ ...prev, ability: e.target.value as any }))}
                  className="w-full px-3 py-2 bg-white border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">Alle</option>
                  <option value="clickMultiplier">Klick-Multiplikator</option>
                  <option value="coinPerSecond">Münzen pro Sekunde</option>
                  <option value="gemChance">Edelstein-Chance</option>
                  <option value="xpBonus">Erfahrungs-Bonus</option>
                  <option value="gardenBonus">Garten-Bonus</option>
                  <option value="autoClickPower">Auto-Klick-Kraft</option>
                  <option value="upgradeDiscount">Upgrade-Rabatt</option>
                  <option value="prestigeBonus">Prestige-Bonus</option>
                  <option value="criticalHit">Kritische Treffer</option>
                  <option value="healing">Heilung</option>
                  <option value="shield">Schild</option>
                  <option value="counterAttack">Gegenangriff</option>
                  <option value="lifeSteal">Lebensentzug</option>
                  <option value="armorPenetration">Rüstungsdurchdringung</option>
                </select>
              </div>

              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-blue-800 mb-2">Status:</label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value as any }))}
                  className="w-full px-3 py-2 bg-white border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">Alle</option>
                  <option value="available">Verfügbar</option>
                  <option value="busy">Beschäftigt</option>
                </select>
              </div>
            </div>

            {/* Reset Button */}
            <div className="flex justify-end">
              <button
                onClick={resetFilters}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                Filter zurücksetzen
              </button>
            </div>
          </div>
        </motion.div>
      </div>

      <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-3 sm:p-6 border border-gray-600">
        
        {availableCompanions.length === 0 ? (
          <div className="text-center text-gray-400 py-6 sm:py-8">
            <div className="text-3xl sm:text-4xl mb-3 sm:mb-4">😴</div>
            <p className="text-sm sm:text-base">
              {getFilteredCompanions().length === 0 && companions.discovered.length > 0 
                ? 'Keine Begleiter entsprechen den aktuellen Filtern!' 
                : 'Alle Begleiter sind aktiv oder beschäftigt!'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {availableCompanions.map((companion) => {
              const status = getCompanionStatus(companion);
              const timeRemaining = getTimeRemaining(companion);
              const level = getCompanionLevel(companion);
              const experience = getCompanionExperience(companion);
              const nextLevelXP = getCompanionExperienceForNextLevel(level);
              const canLevelUp = canCompanionLevelUp(companion);
              
              return (
                <motion.div
                  key={companion.id}
                  className={`relative overflow-hidden rounded-2xl cursor-pointer min-h-[240px] max-w-full group
                    ${isCompanionBusy(companion) ? "opacity-60" : "shadow-lg hover:shadow-xl"}
                    transition-all duration-300 ease-out`}
                  whileHover={!isCompanionBusy(companion) ? { 
                    scale: 1.03,
                    y: -5,
                    transition: { duration: 0.2 }
                  } : {}}
                  whileTap={!isCompanionBusy(companion) ? { scale: 0.97 } : {}}
                  onClick={() => !isCompanionBusy(companion) && setSelectedCompanion(companion)}
                >
                  {/* Hintergrund-Gradient */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${getRarityColor(companion.type)} opacity-90`} />
                  
                  {/* Glanz-Effekt */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  
                  {/* Status-Indikator */}
                  <div className={`absolute top-3 right-3 ${status.color} text-white text-xs px-3 py-1.5 rounded-full font-bold shadow-lg z-10`}>
                    {status.text}
                    {timeRemaining && ` (${timeRemaining})`}
                  </div>

                  {/* Göttlich-Indikator */}
                  {isGodlyLegendary(companion) && (
                    <div className="absolute top-3 left-3 bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-xs px-3 py-1.5 rounded-full font-bold shadow-lg z-10 animate-pulse">
                      ✨ PERFEKT ✨
                    </div>
                  )}

                  {/* Mystisch-Indikator */}
                  {isMysticDivine(companion) && (
                    <div className="absolute top-3 left-3 bg-gradient-to-r from-indigo-400 to-purple-500 text-white text-xs px-3 py-1.5 rounded-full font-bold shadow-lg z-10 animate-pulse">
                      💫 MYSTISCH 💫
                    </div>
                  )}

                  {/* Hauptinhalt */}
                  <div className="relative z-10 p-4 h-full flex flex-col justify-between">
                    {/* Icon und Name */}
                    <div className="text-center mb-3">
                      <div className="text-4xl sm:text-5xl mb-2">
                        {companion.icon.startsWith('data:') ? (
                          <img 
                            src={companion.icon} 
                            alt={companion.name}
                            className="w-12 h-12 sm:w-16 sm:h-16 object-contain"
                            style={{ display: 'inline-block' }}
                          />
                        ) : (
                          companion.icon
                        )}
                      </div>
                      <h3 className="text-lg font-bold text-white mb-1 leading-tight">
                        {companion.name}
                      </h3>
                      <div className="flex items-center justify-center gap-2">
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                          isGodlyLegendary(companion) 
                            ? 'bg-yellow-400 text-yellow-900' 
                            : companion.type === 'legendary' 
                              ? 'bg-yellow-500 text-yellow-900' 
                              : companion.type === 'epic' 
                                ? 'bg-purple-500 text-white' 
                                : companion.type === 'rare' 
                                  ? 'bg-blue-500 text-white' 
                                  : 'bg-gray-500 text-white'
                        }`}>
                                                      {isMysticDivine(companion) ? 'Mystisch' : isGodlyLegendary(companion) ? 'Göttlich' : getRarityName(companion.type)}
                        </span>
                        <span className="text-xs bg-white/20 text-white px-2 py-1 rounded-full">
                          Lv. {level}
                        </span>
                      </div>
                    </div>

                    {/* Fähigkeiten */}
                    <div className="space-y-2 mb-3">
                      {companion.abilities.slice(0, 3).map((ability, index) => (
                        <div key={index} className="flex items-center justify-between text-xs">
                          <div className="flex items-center gap-1">
                            <span>{getAbilityIcon(ability.type)}</span>
                            <span className="text-white/80">{getAbilityName(ability.type)}</span>
                          </div>
                          <span className="text-white font-bold">
                            {ability.value}{getAbilitySuffix(ability.type)}
                          </span>
                        </div>
                      ))}
                      {companion.abilities.length > 3 && (
                        <div className="text-center text-white/60 text-xs">
                          +{companion.abilities.length - 3} weitere
                        </div>
                      )}
                    </div>

                    {/* Level-Up Button */}
                    {canLevelUp && (
                      <motion.button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleLevelUp(companion);
                        }}
                        className="w-full bg-green-600 hover:bg-green-700 text-white py-2 px-3 rounded-lg text-sm font-bold mb-2 transition-colors"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        ⬆️ Level Up!
                      </motion.button>
                    )}

                    {/* XP-Balken */}
                    <div className="mb-3">
                      <div className="flex justify-between text-xs text-white/80 mb-1">
                        <span>XP: {experience}/{nextLevelXP}</span>
                        <span>{Math.round((experience / nextLevelXP) * 100)}%</span>
                      </div>
                      <div className="w-full bg-black/30 rounded-full h-2">
                        <div 
                          className="bg-gradient-to-r from-blue-400 to-purple-500 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${(experience / nextLevelXP) * 100}%` }}
                        />
                      </div>
                    </div>

                    <div className="text-center">
                      <div className="text-white/60 text-xs">
                        {isCompanionBusy(companion) ? 'Beschäftigt' : 'Klicken für Aktivität'}
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Aktivitäts-Modal */}
      <AnimatePresence key="companion-training-modal">
        {selectedCompanion && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-3 sm:p-4"
            onClick={() => setSelectedCompanion(null)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-4 sm:p-6 max-w-md w-full border border-gray-600 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center mb-4 sm:mb-6">
                <div className="text-5xl sm:text-4xl mb-2">
                  {selectedCompanion.icon.startsWith('data:') ? (
                    <img 
                      src={selectedCompanion.icon} 
                      alt={selectedCompanion.name}
                      className="w-16 h-16 sm:w-20 sm:h-20 object-contain"
                      style={{ display: 'inline-block' }}
                    />
                  ) : (
                    selectedCompanion.icon
                  )}
                </div>
                <h3 className="text-xl font-bold text-white">{selectedCompanion.name}</h3>
                <p className="text-gray-400">Level {getCompanionLevel(selectedCompanion)}</p>
              </div>

              {/* Aktivitäts-Auswahl */}
              <div className="mb-4 sm:mb-6">
                {isMysticDivine(selectedCompanion) ? (
                  <div className="text-center py-6 sm:py-8">
                    <div className="text-6xl mb-4">💫</div>
                    <h4 className="text-xl font-bold text-indigo-400 mb-2">Mystischer Begleiter</h4>
                    <p className="text-gray-300 text-sm">
                      Dieser Begleiter hat bereits die mystische Perfektion erreicht!
                    </p>
                    <p className="text-gray-400 text-xs mt-2">
                      Training und Reisen sind möglich, aber nicht notwendig.
                    </p>
                  </div>
                ) : isGodlyLegendary(selectedCompanion) ? (
                  <div className="text-center py-6 sm:py-8">
                    <div className="text-6xl mb-4">✨</div>
                    <h4 className="text-xl font-bold text-yellow-400 mb-2">Göttlicher Begleiter</h4>
                    <p className="text-gray-300 text-sm">
                      Dieser Begleiter hat bereits die Perfektion erreicht!
                    </p>
                    <p className="text-gray-400 text-xs mt-2">
                      Training und Reisen sind möglich, aber nicht notwendig.
                    </p>
                  </div>
                ) : null}

                <div className="flex space-x-2 mb-4">
                  <button
                    onClick={() => setActivityType('training')}
                    className={`flex-1 py-3 px-4 rounded-lg font-medium transition-colors min-h-[48px] touch-manipulation ${
                      activityType === 'training'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600 active:bg-gray-600'
                    }`}
                  >
                    🎓 Training
                  </button>
                  <button
                    onClick={() => setActivityType('journey')}
                    className={`flex-1 py-3 px-4 rounded-lg font-medium transition-colors min-h-[48px] touch-manipulation ${
                      activityType === 'journey'
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600 active:bg-gray-600'
                    }`}
                  >
                    🗺️ Reise
                  </button>
                </div>

                {activityType === 'training' ? (
                  <div className="space-y-4">
                    <p className="text-gray-300 text-sm text-center">
                      Training verbessert die Erfahrung deines Begleiters
                    </p>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <button
                        onClick={() => handleStartTraining(30)}
                        disabled={coins < 300}
                        className="bg-blue-600 hover:bg-blue-700 active:bg-blue-800 disabled:bg-gray-600 disabled:cursor-not-allowed text-white py-3 px-3 rounded-lg text-sm min-h-[60px] touch-manipulation"
                      >
                        <div className="font-medium">30 Min</div>
                        <div className="text-xs opacity-80">300 💰 • +60 XP</div>
                      </button>
                      <button
                        onClick={() => handleStartTraining(60)}
                        disabled={coins < 600}
                        className="bg-blue-600 hover:bg-blue-700 active:bg-blue-800 disabled:bg-gray-600 disabled:cursor-not-allowed text-white py-3 px-3 rounded-lg text-sm min-h-[60px] touch-manipulation"
                      >
                        <div className="font-medium">1 Std</div>
                        <div className="text-xs opacity-80">600 💰 • +120 XP</div>
                      </button>
                      <button
                        onClick={() => handleStartTraining(120)}
                        disabled={coins < 1200}
                        className="bg-blue-600 hover:bg-blue-700 active:bg-blue-800 disabled:bg-gray-600 disabled:cursor-not-allowed text-white py-3 px-3 rounded-lg text-sm min-h-[60px] touch-manipulation"
                      >
                        <div className="font-medium">2 Std</div>
                        <div className="text-xs opacity-80">1.2K 💰 • +240 XP</div>
                      </button>
                      <button
                        onClick={() => handleStartTraining(240)}
                        disabled={coins < 2400}
                        className="bg-blue-600 hover:bg-blue-700 active:bg-blue-800 disabled:bg-gray-600 disabled:cursor-not-allowed text-white py-3 px-3 rounded-lg text-sm min-h-[60px] touch-manipulation"
                      >
                        <div className="font-medium">4 Std</div>
                        <div className="text-xs opacity-80">2.4K 💰 • +480 XP</div>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Reise-Typ Auswahl */}
                    <div>
                      <label className="block text-gray-300 text-sm mb-2 font-medium">Reise-Art:</label>
                      <select
                        value={journeyType}
                        onChange={(e) => setJourneyType(e.target.value as any)}
                        className="w-full bg-gray-700 text-white rounded-lg p-3 border border-gray-600 min-h-[48px] text-sm touch-manipulation"
                      >
                        <option value="treasure">💰 Schatzsuche (Münzen & Edelsteine)</option>
                        <option value="experience">⭐ Erkundung (Spieler-XP)</option>
                        <option value="renovation">🏗️ Renovierung (Auto-Upgrade)</option>
                      </select>
                    </div>

                    {/* Bereich-Auswahl für Renovierung */}
                    {journeyType === 'renovation' && (
                      <div>
                        <label className="block text-gray-300 text-sm mb-2 font-medium">Bereich:</label>
                        <select
                          value={selectedArea}
                          onChange={(e) => setSelectedArea(e.target.value as any)}
                          className="w-full bg-gray-700 text-white rounded-lg p-3 border border-gray-600 min-h-[48px] text-sm touch-manipulation"
                        >
                          {Object.entries(areaNames).map(([key, name]) => (
                            <option key={key} value={key}>{name}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Dauer-Optionen */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <button
                        onClick={() => handleStartJourney(1)}
                        disabled={coins < 100}
                        className="bg-purple-600 hover:bg-purple-700 active:bg-purple-800 disabled:bg-gray-600 disabled:cursor-not-allowed text-white py-3 px-3 rounded-lg text-sm min-h-[60px] touch-manipulation"
                      >
                        <div className="font-medium">1 Std</div>
                        <div className="text-xs opacity-80">100 💰</div>
                      </button>
                      <button
                        onClick={() => handleStartJourney(4)}
                        disabled={coins < 400}
                        className="bg-purple-600 hover:bg-purple-700 active:bg-purple-800 disabled:bg-gray-600 disabled:cursor-not-allowed text-white py-3 px-3 rounded-lg text-sm min-h-[60px] touch-manipulation"
                      >
                        <div className="font-medium">4 Std</div>
                        <div className="text-xs opacity-80">400 💰</div>
                      </button>
                      <button
                        onClick={() => handleStartJourney(8)}
                        disabled={coins < 800}
                        className="bg-purple-600 hover:bg-purple-700 active:bg-purple-800 disabled:bg-gray-600 disabled:cursor-not-allowed text-white py-3 px-3 rounded-lg text-sm min-h-[60px] touch-manipulation"
                      >
                        <div className="font-medium">8 Std</div>
                        <div className="text-xs opacity-80">800 💰</div>
                      </button>
                      <button
                        onClick={() => handleStartJourney(24)}
                        disabled={coins < 2400}
                        className="bg-purple-600 hover:bg-purple-700 active:bg-purple-800 disabled:bg-gray-600 disabled:cursor-not-allowed text-white py-3 px-3 rounded-lg text-sm min-h-[60px] touch-manipulation"
                      >
                        <div className="font-medium">24 Std</div>
                        <div className="text-xs opacity-80">2.4K 💰</div>
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Schließen Button */}
              <button
                onClick={() => setSelectedCompanion(null)}
                className="w-full bg-gray-600 hover:bg-gray-700 active:bg-gray-800 text-white py-3 px-4 rounded-lg min-h-[48px] touch-manipulation font-medium"
              >
                Schließen
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CompanionTraining;
